import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { motion, useReducedMotion, type Transition } from 'framer-motion'

// A clip edge flush with the last row's 1px border loses it to pixel snapping,
// which moves the edge by under a pixel. One pixel of slack keeps it painted.
const CLIP_SLACK_PX = 1

const HEIGHT_TRANSITION: Transition = { duration: 0.25, ease: [0.32, 0.72, 0, 1] }
const INSTANT: Transition = { duration: 0 }

/**
 * Layout height from ResizeObserver's `contentRect`, not `getBoundingClientRect`.
 * The rect includes ancestor transforms, so a card mounting inside a scale-in
 * entrance would lock in a height a few percent short, and no resize follows
 * once the scale settles to correct it.
 */
function useLayoutHeight(): [(node: HTMLDivElement | null) => void, number] {
  const [node, setNode] = useState<HTMLDivElement | null>(null)
  const [height, setHeight] = useState(0)

  useLayoutEffect(() => {
    if (!node) {
      return
    }
    const observer = new ResizeObserver(([entry]) => {
      setHeight(entry.contentRect.height)
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [node])

  return [setNode, height]
}

/**
 * Wraps a card body and animates its height whenever the content reflows — a
 * step change, a text box growing, the resolved summary replacing the form.
 * Measured with a ResizeObserver and driven by framer-motion, which retargets
 * an interrupted resize smoothly instead of restarting it. Height is a layout
 * property, but this is one container resizing a few times per card, not a
 * per-frame animation.
 */
export function AnimatedHeight({ children }: { children: ReactNode }) {
  const [ref, measured] = useLayoutHeight()
  const reduceMotion = useReducedMotion()

  // Layout heights are fractional. Rounding up keeps the clip box from ending
  // inside the content.
  const height = measured > 0 ? Math.ceil(measured) + CLIP_SLACK_PX : undefined

  // The target must be defined from the first render: with `initial={false}`
  // framer treats the first `animate` it sees as already applied and drops it,
  // so a target that only appears after measuring would never take effect.
  const animate = useMemo(() => ({ height: height ?? 'auto' }), [height])

  // Mount renders at `auto`. The first measurement swaps in a pixel height
  // that differs only by the clip slack, so it is applied instantly rather
  // than animated; every reflow after that animates.
  const hasMeasuredRef = useRef(false)
  const instant = reduceMotion || !hasMeasuredRef.current
  useEffect(() => {
    if (height !== undefined) {
      hasMeasuredRef.current = true
    }
  }, [height])

  return (
    <motion.div
      initial={false}
      animate={animate}
      transition={instant ? INSTANT : HEIGHT_TRANSITION}
      // shrink-0: a clipping flex item has no automatic minimum, so a
      // height-constrained parent would squeeze this box (cutting the last
      // row) instead of overflowing into its own scroller.
      className="shrink-0 overflow-hidden"
    >
      <div ref={ref}>{children}</div>
    </motion.div>
  )
}
